import { max, min } from 'lodash'
import { TrendingDown, TrendingUp } from 'lucide-react'
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts'

import { CardContent, CardDescription, CardHeader } from '@/components/card'
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/chart'

import { cn } from '@/utils/cn'

export type DataPoint = { time: string; price?: string | null }

interface Props {
    data: DataPoint[]
}

export default function HistoryChart({ data }: Props) {
    const priceData = data.filter(d => d.price !== null).map(d => Number(d.price))
    const minY = min(priceData)
    const maxY = max(priceData)

    const startingPrice = priceData.length ? priceData[0] : null
    const difference = startingPrice ? priceData[priceData.length - 1] - startingPrice : null
    const percentChange = startingPrice && difference !== null ? (difference / startingPrice) * 100 : null
    const showPositiveChange = percentChange && percentChange > 0

    const chartConfig = {
        price: {
            label: 'Close price',
            color: showPositiveChange ? 'hsl(var(--green))' : 'hsl(var(--red))',
        },
    } satisfies ChartConfig

    return (
        <>
            <CardHeader>
                <CardDescription
                    className={cn('flex items-center gap-2', {
                        'text-success': showPositiveChange,
                        'text-destructive': !showPositiveChange,
                    })}
                >
                    {difference && percentChange ? `${difference.toFixed(3)} (${percentChange.toFixed(3)}%)` : '--'}
                    {percentChange !== null && (
                        <>
                            {showPositiveChange && <TrendingUp className="w-4 h-4" />}
                            {!showPositiveChange && <TrendingDown className="w-4 h-4" />}
                        </>
                    )}
                </CardDescription>
            </CardHeader>
            <CardContent className="pb-0">
                <ChartContainer config={chartConfig}>
                    <AreaChart data={data}>
                        <CartesianGrid vertical={false} />
                        <YAxis
                            type="number"
                            tickLine={false}
                            axisLine={false}
                            domain={[minY || 0, maxY || 1]}
                            allowDataOverflow
                        />
                        <XAxis dataKey="day" tickLine={false} axisLine={false} />
                        <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="line" />} />
                        <Area
                            dataKey="price"
                            type="linear"
                            fill="var(--color-price)"
                            fillOpacity={0.2}
                            stroke="var(--color-price)"
                            isAnimationActive={false}
                        />
                    </AreaChart>
                </ChartContainer>
            </CardContent>
        </>
    )
}
